"use client";

import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useQuery } from "react-query";
import { getCalculations } from "../apis";

// Mock API call to get tax rates
const fetchTaxRates = async () => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return {
    cgst: 0.09,
    sgst: 0.09,
  };
};

export function CalculationPanel() {
  const { control } = useFormContext();
  const customers = useWatch({ control, name: "customers", defaultValue: [] });

  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [productBreakdown, setProductBreakdown] = useState<
    Array<{
      label: string;
      currency: string;
      amount: number;
      value: number;
      rate: number;
    }>
  >([]);

  const [calculations, setCalculations] = useState({
    subtotal: 0,
    cgst: 0,
    sgst: 0,
    roundOff: 0,
    grandTotal: 0,
  });

  const { data: taxRates } = useQuery({
    queryKey: ["taxRates"],
    queryFn: fetchTaxRates,
    initialData: { cgst: 0.09, sgst: 0.09 },
  });

  useEffect(() => {
    const allProducts: any[] = [];
    customers.forEach((customer: any) => {
      const products = customer?.products || [];
      products.forEach((product: any) => {
        allProducts.push({
          customer: customer?.fullName || "Unknown",
          product,
        });
      });
    });

    const groupedProducts: Record<
      string,
      { label: string; currency: string; amount: number; value: number , rate: number}
    > = {};

    allProducts.forEach(({ product }) => {
      const productType = product?.productType?.label || "Unknown";
      const currencies = product?.currencies || [];

      currencies.forEach((currency: any) => {
        const currencyCode =
          currency?.currency?.value?.toUpperCase() || "UNKNOWN";
        const amount = currency?.amount || 0;
        const rate = currency?.rate || 1;
        const value = amount * rate;
        const key = `${currencyCode} ${productType}`;

        if (!groupedProducts[key]) {
          groupedProducts[key] = {
            label: key,
            currency: "INR",
            amount: 0,
            value: 0,
            rate: rate,
          };
        }

        groupedProducts[key].amount += amount;
        groupedProducts[key].value += value;
      });
    });

    const productList = Object.values(groupedProducts);
    setProductBreakdown(productList);

    const subtotal = productList.reduce((sum, item) => sum + item.value, 0);
    const cgst = subtotal * (taxRates?.cgst || 0);
    const sgst = subtotal * (taxRates?.sgst || 0);
    const total = subtotal + cgst + sgst;
    const roundedTotal = Math.round(total);
    const roundOff = roundedTotal - total;

    setCalculations({
      subtotal,
      cgst,
      sgst,
      roundOff,
      grandTotal: roundedTotal,
    });
  }, [customers, taxRates]);

  const productCount = productBreakdown.length;

  const { data: additionalCharges } = useQuery({
    queryKey: ["additionalCharges", productCount],
    queryFn: () =>
      getCalculations({ amount: calculations.grandTotal, type: "buy" }),
    enabled: productCount > 0,
  });

  const isLoadingAdditionalCharges = !additionalCharges && productCount > 0;

  // Don't render the panel if there are no products
  if (productCount === 0) {
    return null;
  }

  return (
    <div className="w-full bg-white rounded-xl p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
      <h2 className="text-2xl font-bold mb-6">Calculation</h2>

      <div className="flex flex-col gap-8">
        {/* Product breakdown */}
        <div className="flex flex-col gap-3">
          {productBreakdown?.map((item, index) => (
            <div key={index}>
              <div className="flex justify-between items-center">
                <span className="font-inter font-medium text-sm leading-none tracking-[-0.04em] text-gray-800">
                  <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">{item?.label}</span> { "  "}{item?.amount} <span className="text-[#5777B2]">@</span> <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">INR</span> {item?.rate.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <div className="flex items-center">
                  <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md mr-2">
                    {item?.currency}
                  </span>
                  <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
                    {item?.value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
              {index !== productBreakdown.length - 1 && (
                <div className="w-full h-px bg-gray-100" />
              )}
            </div>
          ))}
        </div>

        {/* Additional charges section */}
        <div className="flex flex-col gap-5">
          <div
            className="flex items-center justify-between h-[23px] rounded-[12px] pt-1 pr-3 pb-1 pl-3 bg-gray-50 font-medium cursor-pointer select-none"
            onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
          >
            <span className="font-medium">Additional charges</span>
            {showAdditionalCharges ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>

          {showAdditionalCharges && (
            <div className="space-y-0 mt-2">
              {isLoadingAdditionalCharges ? (
                <div className="text-center text-gray-400 py-4">
                  Loading charges...
                </div>
              ) : !additionalCharges ? (
                <div className="text-center text-gray-400 py-4">
                  No additional charges available
                </div>
              ) : (
                <>
                  <ChargeRow
                    label={`CGST @ ${(taxRates?.cgst || 0) * 100}%`}
                    amount={additionalCharges?.CGST}
                  />
                  <ChargeRow
                    label={`SGST @ ${(taxRates?.sgst || 0) * 100}%`}
                    amount={additionalCharges?.SGST}
                  />
                  <ChargeRow
                    label="Round off"
                    amount={additionalCharges?.roundOff}
                  />
                </>
              )}
            </div>
          )}

          {/* Grand Total */}
          <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] border-t-2 border-[#176bef] rounded-b-xl">
            <span className="font-inter font-semibold text-base leading-none text-[#176bef]">
              Grand Total
            </span>
            <div className="flex items-center">
              <span className="bg-[#176bef] text-white-A700 w-[29px] h-[19px] rounded-[8px] px-[6px] flex items-center justify-center">
                <span className="font-inter font-normal text-[10px] leading-[1.12] tracking-[-0.01em]">
                  INR
                </span>
              </span>
              <span className="font-inter font-medium text-base leading-[1.5] text-[#176bef] ml-3">
                {additionalCharges?.total_payable?.grand_total
                  ? additionalCharges.total_payable.grand_total.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )
                  : "0.00"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChargeRow({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100">
      <span className="font-inter font-medium text-xs leading-none text-gray-700">
        {label}
      </span>
      <div className="flex items-center">
        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md mr-2">
          INR
        </span>
        <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
          {amount.toFixed(2)}
        </span>
      </div>
    </div>
  );
}
